import React, { useState } from 'react';

interface PresentersListProps {
  presenters: string[];
  currentUser?: string;
  displayMode: 'rows' | 'inline'; // rows = separate lines, inline = same line
  maxVisible?: number; // For rows mode, default 4
}

const PresentersList: React.FC<PresentersListProps> = ({ 
  presenters, 
  currentUser,
  displayMode,
  maxVisible = 4
}) => {
  const [showAll, setShowAll] = useState(false);

  // Debug logging
  console.log('PresentersList:', { presenters, currentUser, displayMode });

  if (!presenters || presenters.length === 0) {
    return <span>-</span>;
  }

  // Sort presenters: current user first, then alphabetical
  const sortedPresenters = [...presenters].sort((a, b) => {
    if (currentUser) {
      if (a === currentUser) return -1;
      if (b === currentUser) return 1;
    }
    return a.localeCompare(b);
  });

  // Phonetool link component
  const PhoneToolLink: React.FC<{ alias: string }> = ({ alias }) => (
    <a
      href={`https://phonetool.amazon.com/users/${alias}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: '#2563eb',
        textDecoration: 'none',
        fontWeight: '500'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {alias}
    </a>
  );

  if (displayMode === 'rows') {
    // Display on separate lines with "...more/...less" link
    const visiblePresenters = showAll ? sortedPresenters : sortedPresenters.slice(0, maxVisible);
    const hasMore = sortedPresenters.length > maxVisible;

    return (
      <div>
        {visiblePresenters.map((presenter, index) => (
          <div key={index}>
            <PhoneToolLink alias={presenter} />
          </div>
        ))}
        {hasMore && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              setShowAll(!showAll);
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#2563eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#6b7280';
            }}
            style={{
              color: '#6b7280',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.875em'
            }}
          >
            {showAll ? '...less' : '...more'}
          </span>
        )}
      </div>
    );
  } else {
    // Display inline with "...and N more/...less" link
    const visiblePresenters = showAll ? sortedPresenters : sortedPresenters.slice(0, 3);
    const remainingCount = sortedPresenters.length - 3;
    const hasMore = sortedPresenters.length > 3;

    return (
      <span>
        {visiblePresenters.map((presenter, index) => (
          <React.Fragment key={index}>
            {index > 0 && ', '}
            <PhoneToolLink alias={presenter} />
          </React.Fragment>
        ))}
        {hasMore && !showAll && (
          <>
            {', '}
            <span
              onClick={(e) => {
                e.stopPropagation();
                setShowAll(true);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#6b7280';
              }}
              style={{
                color: '#6b7280',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.875em'
              }}
            >
              ...and {remainingCount} more
            </span>
          </>
        )}
        {hasMore && showAll && (
          <>
            {' '}
            <span
              onClick={(e) => {
                e.stopPropagation();
                setShowAll(false);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#6b7280';
              }}
              style={{
                color: '#6b7280',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.875em'
              }}
            >
              ...less
            </span>
          </>
        )}
      </span>
    );
  }
};

export default PresentersList;
